import { startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const GRID_VIEW_STORAGE_PREFIX = 'product-grid-view-';
const ALLOWED_GRID_VIEWS = {
  desktop: new Set(['desktop-2', 'desktop-3', 'desktop-4']),
  mobile: new Set(['mobile-single', 'default'])
};
const DEPICT_SYNC_RETRY_DELAYS_MS = [0, 50, 150, 400];

// Match product-grid CSS + filters density controls (Tailwind `lg` / 1024px).
const productGridMediaQuery = matchMedia('(min-width: 1024px)');

/**
 * @param {'desktop' | 'mobile'} viewport
 * @returns {string}
 */
function gridViewStorageKey(viewport) {
  return `${GRID_VIEW_STORAGE_PREFIX}${viewport}`;
}

/**
 * @returns {'desktop' | 'mobile'}
 */
function currentViewport() {
  return productGridMediaQuery.matches ? 'desktop' : 'mobile';
}

/**
 * @param {'desktop' | 'mobile'} viewport
 * @param {string | null | undefined} value
 * @returns {value is string}
 */
function isAllowedGridView(viewport, value) {
  return typeof value === 'string' && ALLOWED_GRID_VIEWS[viewport].has(value);
}

/**
 * @param {'desktop' | 'mobile'} viewport
 * @returns {string | null}
 */
function readSavedGridView(viewport) {
  try {
    const value = localStorage.getItem(gridViewStorageKey(viewport));
    return isAllowedGridView(viewport, value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * @param {'desktop' | 'mobile'} viewport
 * @param {string} value
 */
function writeSavedGridView(viewport, value) {
  if (!isAllowedGridView(viewport, value)) return;

  try {
    localStorage.setItem(gridViewStorageKey(viewport), value);
  } catch {
    // Ignore quota / private mode failures.
  }
}

/**
 * @param {HTMLElement} grid
 * @returns {number}
 */
function columnCountFromGrid(grid) {
  // Mobile is only 1-up or 2-up — never trust a stale desktop-* column count.
  if (!productGridMediaQuery.matches) {
    const view = grid.getAttribute('product-grid-view');
    if (view === 'mobile-single') return 1;
    const fromData = Number(grid.getAttribute('data-grid-columns'));
    if (fromData === 1) return 1;
    return 2;
  }

  const fromData = Number(grid.getAttribute('data-grid-columns'));
  if (Number.isFinite(fromData) && fromData > 0) return fromData;

  const view = grid.getAttribute('product-grid-view');
  if (view === 'desktop-2') return 2;
  if (view === 'desktop-3') return 3;
  if (view === 'desktop-4') return 4;
  if (view === 'default') return 4;

  return 4;
}

/**
 * Marks each product card with `data-grid-col` from on-screen position.
 * Slot/DOM index is unreliable once Depict injects beacons or merch blocks — that
 * briefly assigned column 2 to a visual column-1 card and zeroed its info padding.
 * Sets `[data-depict-full-row]` so CSS can add extra space under image-only full-bleed merch.
 *
 * @param {HTMLElement | null | undefined} grid
 */
export function syncProductGridColumnAttrs(grid) {
  if (!(grid instanceof HTMLElement)) return;

  const columns = columnCountFromGrid(grid);
  const gridWidth = grid.clientWidth;
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.columnGap) || 0;
  const track = columns > 0 && gridWidth > 0 ? (gridWidth - gap * Math.max(0, columns - 1)) / columns : 0;
  const gridLeft = grid.getBoundingClientRect().left;

  for (const child of grid.children) {
    if (!(child instanceof HTMLElement)) continue;

    const tag = child.tagName;
    if (tag === 'SCRIPT' || tag.endsWith('-BEACON')) continue;

    const isContentBlock = child.classList.contains('depict-content-block');
    const isProduct =
      child.classList.contains('product-grid__item') || (tag === 'LI' && child.hasAttribute('data-product-id'));
    if (!isContentBlock && !isProduct) continue;

    if (isProduct) {
      let col = 1;
      if (columns > 1 && track > 0) {
        const relativeLeft = child.getBoundingClientRect().left - gridLeft;
        col = Math.min(columns, Math.max(1, Math.floor((relativeLeft + 1) / (track + gap)) + 1));
      }
      child.setAttribute('data-grid-col', String(col));
      child.removeAttribute('data-depict-full-row');
      continue;
    }

    child.removeAttribute('data-grid-col');

    let span = columns;
    let hasExplicitSpan = false;
    const rawSpan = Number.parseInt(
      child.style.getPropertyValue('--grid-column-span') ||
        getComputedStyle(child).getPropertyValue('--grid-column-span'),
      10
    );
    if (Number.isFinite(rawSpan) && rawSpan > 0) {
      hasExplicitSpan = true;
      span = Math.min(rawSpan, columns);
    }

    const fullBySpan = hasExplicitSpan && span >= columns;
    const fullByWidth = gridWidth > 0 && child.offsetWidth >= gridWidth - 2;
    if (fullBySpan || fullByWidth) {
      child.setAttribute('data-depict-full-row', '');
    } else {
      child.removeAttribute('data-depict-full-row');
    }
  }
}

/**
 * Merch sizing helpers for Depict content blocks.
 * Desktop: seed `--plp-merch-row-span` and clear stale alignment after density changes.
 * Mobile: force 3/4 and clear Depict card-height padding (inline vars).
 * @param {HTMLElement} grid
 */
export function syncDepictContentBlockImageAlign(grid) {
  if (!(grid instanceof HTMLElement)) return;

  if (!productGridMediaQuery.matches) {
    for (const block of grid.querySelectorAll('.depict-content-block')) {
      if (!(block instanceof HTMLElement)) continue;
      block.style.setProperty('--bottom-alignment-spacing', '0px');
      block.style.setProperty('--top-alignment-spacing', '0px');
      block.style.setProperty('--content-aspect-ratio', '3 / 4', 'important');
      block.style.setProperty('aspect-ratio', '3 / 4', 'important');
      block.style.setProperty('height', 'auto', 'important');
      block.style.setProperty('align-self', 'start', 'important');

      const media = block.querySelector('.depict-block-media');
      if (!(media instanceof HTMLElement)) continue;
      media.style.setProperty('margin-bottom', '0px', 'important');
      media.style.setProperty('margin-top', '0px', 'important');
      if (media.style.height) media.style.removeProperty('height');
    }
    return;
  }

  const depictScript = window['depict-grid-rendering-script'];
  if (depictScript?.dataset?.contentBlocks) {
    try {
      const blocks = JSON.parse(depictScript.dataset.contentBlocks);
      if (Array.isArray(blocks)) {
        for (const block of blocks) {
          const span = Number(block?.span_rows);
          if (span > 0) {
            grid.style.setProperty('--plp-merch-row-span', String(span));
            break;
          }
        }
      }
    } catch {
      // Ignore invalid Depict JSON.
    }
  }

  for (const block of grid.querySelectorAll('.depict-content-block')) {
    if (!(block instanceof HTMLElement)) continue;
    if (block.style.height) block.style.removeProperty('height');
    block.style.setProperty('--bottom-alignment-spacing', '0px');
    block.style.setProperty('--top-alignment-spacing', '0px');

    const media = block.querySelector('.depict-block-media');
    if (!(media instanceof HTMLElement)) continue;
    media.style.setProperty('margin-bottom', '0px', 'important');
    media.style.setProperty('margin-top', '0px', 'important');
    if (media.style.height) media.style.removeProperty('height');
  }
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #resizeObserver = new ResizeObserver(() => {
    this.#syncDepictGaps();
    this.#reconcileLayoutForViewport();
  });

  /** @type {MutationObserver | null} */
  #gridColumnObserver = null;

  /** @type {string | null} */
  #pendingDepictLayout = null;

  /** @type {number[]} */
  #depictRetryTimeouts = [];

  /** @type {'desktop' | 'mobile' | null} */
  #lastViewport = null;

  connectedCallback() {
    super.connectedCallback();

    productGridMediaQuery.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#lastViewport = currentViewport();
    this.#syncDepictGaps();

    if (this.#shouldRememberGridView() && this.#restoreSavedLayout()) {
      // Restored from localStorage for this viewport.
    } else {
      const viewport = currentViewport();
      const { grid } = this.refs;
      const current = grid?.getAttribute('product-grid-view');

      // Prefer the blocking pref script's already-correct view so we do not
      // re-layout after first paint (mobile used to flash desktop-* → default).
      if (current && isAllowedGridView(viewport, current)) {
        this.#syncDepictColumns(current);
        this.#syncDepictGridColumnsCss(current);
        this.#scheduleDepictColumnRetries(current);
      } else {
        this.#applyViewportLayout(viewport);
      }
    }

    this.#syncGridColumnAttrs();
    this.#assertContentBlockImageAlign();
    this.#observeGridColumns();
    this.#resizeObserver.observe(document.documentElement);
  }

  disconnectedCallback() {
    productGridMediaQuery.removeEventListener('change', this.#handleMediaQueryChange);
    this.#resizeObserver.disconnect();
    this.#gridColumnObserver?.disconnect();
    this.#gridColumnObserver = null;
    this.#clearDepictRetries();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Persist immediately so a resize/reconcile cannot restore a stale value.
    if (this.#shouldRememberGridView()) {
      writeSavedGridView(currentViewport(), value);
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
  };

  /**
   * Whether this results list should persist grid density in localStorage.
   *
   * @returns {boolean}
   */
  #shouldRememberGridView() {
    return this.hasAttribute('remember-grid-view');
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    // Capture before column CSS changes — afterward scrollLeft maps to the wrong slide
    // and sync can advance `current` before we reseat.
    const slideshowIndexes = [...grid.querySelectorAll('slideshow-component.product-card-slideshow')].map((el) => ({
      el,
      index: typeof el.current === 'number' ? el.current : 0
    }));

    grid.setAttribute('product-grid-view', value);

    const desktopSpans = { 'desktop-2': '6', 'desktop-3': '4', 'desktop-4': '3' };
    if (desktopSpans[value]) {
      grid.style.setProperty('--product-grid-desktop-span', desktopSpans[value]);
    } else {
      // Mobile layouts should not leave a stale desktop span from the previous viewport.
      grid.style.removeProperty('--product-grid-desktop-span');
    }

    this.#syncDepictColumns(value);
    this.#syncDepictGridColumnsCss(value);
    this.#scheduleDepictColumnRetries(value);
    this.#syncGridColumnAttrs();
    this.#syncDepictGaps();
    // Clear stale Depict alignment spacing so CSS height tracks new columns.
    this.#assertContentBlockImageAlign();
    // Flush new card widths, then reseat with pre-change indexes before VT captures "new".
    void grid.offsetWidth;
    for (const { el, index } of slideshowIndexes) {
      if (typeof el.reseatAfterLayoutChange === 'function') {
        el.reseatAfterLayoutChange(index);
      }
    }
  }

  /**
   * Assign `data-grid-col` so mobile card info padding can target visual columns
   * even when Depict merch blocks interrupt product order.
   */
  #syncGridColumnAttrs() {
    syncProductGridColumnAttrs(this.refs.grid);
  }

  /**
   * Re-mark columns when Depict injects merch blocks or infinite scroll appends cards.
   */
  #observeGridColumns() {
    const { grid } = this.refs;
    if (!grid || this.#gridColumnObserver) return;

    this.#gridColumnObserver = new MutationObserver(() => {
      this.#syncGridColumnAttrs();
      this.#assertContentBlockImageAlign();
      // Width-based full-row mark needs layout after Depict injects/spans.
      requestAnimationFrame(() => {
        this.#syncGridColumnAttrs();
        this.#assertContentBlockImageAlign();
      });
    });
    this.#gridColumnObserver.observe(grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  /**
   * Depict owns `.depict-grid { grid-template-columns: repeat(var(--grid-columns), …) }`
   * and unsets per-item spans. Updating dataset.columns* alone does not refresh
   * `--grid-columns` on viewport change — set it explicitly.
   *
   * @param {string} value
   */
  #syncDepictGridColumnsCss(value) {
    const { grid } = this.refs;
    if (!grid) return;

    const desktopColumns = { 'desktop-2': 2, 'desktop-3': 3, 'desktop-4': 4 };
    let columns = desktopColumns[value];

    if (columns == null) {
      if (value === 'mobile-single') columns = 1;
      else if (value === 'default') columns = productGridMediaQuery.matches ? 4 : 2;
      else return;
    }

    grid.style.setProperty('--grid-columns', String(columns), 'important');
    grid.setAttribute('data-grid-columns', String(columns));
  }

  /**
   * Restores a saved grid preference for the current viewport.
   *
   * @returns {boolean} Whether a saved preference was applied
   */
  #restoreSavedLayout() {
    const viewport = currentViewport();
    const saved = readSavedGridView(viewport);
    if (!saved) return false;

    const { grid } = this.refs;
    if (!grid) return false;

    this.#setLayout(saved);
    this.#checkLayoutRadio(viewport, saved);
    return true;
  }

  /**
   * Checks the radio control that matches the given layout value.
   *
   * @param {'desktop' | 'mobile'} viewport
   * @param {string} value
   */
  #checkLayoutRadio(viewport, value) {
    const name = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const input = this.querySelector(`input[name="${name}"][value="${value}"]`);

    if (input instanceof HTMLInputElement) {
      input.checked = true;
      return;
    }

    // Fallbacks used by Liquid for the default options.
    const fallback =
      viewport === 'desktop'
        ? this.querySelector('[data-grid-layout="desktop-default-option"]')
        : this.querySelector('[data-grid-layout="mobile-option"]');

    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
    }
  }

  /**
   * Handles crossing the product-grid desktop breakpoint (1024px).
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.#applyViewportLayout(event.matches ? 'desktop' : 'mobile');
  };

  /**
   * Keep theme + Depict column layout in sync when the viewport crosses 1024px.
   * Also re-applies `--grid-columns` when Depict leaves a stale mobile value after
   * resizing back up to desktop (dataset update alone does not refresh it).
   */
  #reconcileLayoutForViewport() {
    const viewport = currentViewport();
    const { grid } = this.refs;
    if (!grid) return;

    const current = grid.getAttribute('product-grid-view');
    const viewportChanged = this.#lastViewport != null && this.#lastViewport !== viewport;
    this.#lastViewport = viewport;

    // Only swap layout when crossing the breakpoint (or when the attribute is
    // invalid for this viewport). Do not re-apply defaults on every resize —
    // that fought remembered preferences.
    const mismatched =
      (viewport === 'mobile' && !isAllowedGridView('mobile', current)) ||
      (viewport === 'desktop' && !isAllowedGridView('desktop', current));

    if (viewportChanged || mismatched) {
      this.#applyViewportLayout(viewport);
    }

    const view = grid.getAttribute('product-grid-view');
    if (!view) return;

    // Depict updates --grid-columns on resize from its own breakpoint and can
    // overwrite ours; re-assert after its turn.
    this.#syncDepictGridColumnsCss(view);
    this.#syncGridColumnAttrs();
    requestAnimationFrame(() => {
      this.#syncDepictGridColumnsCss(view);
      this.#syncGridColumnAttrs();
      this.#assertContentBlockImageAlign();
    });
    window.setTimeout(() => {
      this.#syncDepictGridColumnsCss(view);
      this.#assertContentBlockImageAlign();
    }, 50);
    window.setTimeout(() => {
      this.#syncDepictGridColumnsCss(view);
      this.#assertContentBlockImageAlign();
    }, 200);
  }

  /**
   * @param {'desktop' | 'mobile'} viewport
   */
  #applyViewportLayout(viewport) {
    let value = this.#shouldRememberGridView() ? readSavedGridView(viewport) : null;
    value ??= this.#defaultLayoutForViewport(viewport);

    if (!isAllowedGridView(viewport, value)) {
      value = this.#defaultLayoutForViewport(viewport);
    }

    this.#lastViewport = viewport;
    this.#setLayout(value);
    this.#checkLayoutRadio(viewport, value);
  }

  /**
   * Fallback layout when nothing is saved for this viewport.
   *
   * @param {'desktop' | 'mobile'} viewport
   * @returns {string}
   */
  #defaultLayoutForViewport(viewport) {
    if (viewport === 'mobile') return 'default';

    const layoutByColumns = { 2: 'desktop-2', 3: 'desktop-3', 4: 'desktop-4' };
    const initialColumns = this.getAttribute('initial-columns-desktop');
    return layoutByColumns[initialColumns] || 'desktop-4';
  }

  /**
   * Maps a layout value to Depict's column dataset properties and updates them.
   *
   * @param {string} value
   * @returns {boolean} Whether Depict was updated
   */
  #syncDepictColumns(value) {
    const depictScript = window['depict-grid-rendering-script'];
    if (!depictScript) return false;

    const desktopColumnMap = { 'desktop-2': 2, 'desktop-3': 3, 'desktop-4': 4 };

    if (value in desktopColumnMap) {
      depictScript.dataset.columnsDesktop = String(desktopColumnMap[value]);
      // Keep Depict's mobile column in sync with the remembered mobile preference
      // (do not force 2 — that cleared mobile-single on every desktop sync).
      this.#syncDepictMobileColumnsFromPreference(depictScript);
    } else if (value === 'mobile-single') {
      depictScript.dataset.columnsMobile = '1';
    } else if (value === 'default') {
      depictScript.dataset.columnsMobile = '2';
      if (productGridMediaQuery.matches) {
        depictScript.dataset.columnsDesktop = '4';
      }
    }

    return true;
  }

  /**
   * @param {HTMLElement} depictScript
   */
  #syncDepictMobileColumnsFromPreference(depictScript) {
    if (this.#shouldRememberGridView()) {
      const mobileSaved = readSavedGridView('mobile');
      if (mobileSaved === 'mobile-single') {
        depictScript.dataset.columnsMobile = '1';
        return;
      }
    }

    depictScript.dataset.columnsMobile = '2';
  }

  /**
   * Retries Depict column sync until the script element exists.
   *
   * @param {string} value
   */
  #scheduleDepictColumnRetries(value) {
    this.#clearDepictRetries();
    this.#pendingDepictLayout = value;

    for (const delay of DEPICT_SYNC_RETRY_DELAYS_MS) {
      const timeoutId = window.setTimeout(() => {
        if (this.#pendingDepictLayout !== value) return;
        if (this.#syncDepictColumns(value)) {
          this.#syncDepictGaps();
          this.#assertContentBlockImageAlign();
          this.#clearDepictRetries();
        }
      }, delay);

      this.#depictRetryTimeouts.push(timeoutId);
    }
  }

  #clearDepictRetries() {
    for (const timeoutId of this.#depictRetryTimeouts) {
      window.clearTimeout(timeoutId);
    }
    this.#depictRetryTimeouts = [];
    this.#pendingDepictLayout = null;
  }

  /**
   * Keep PLP gaps in sync with the page layout grid.
   * Desktop: `--layout-grid-gutter` (24px below 1280px, 64px from 1280px) / 40px row.
   * Mobile: 8px col; row 2-up 16+12=28px, 1-up 24+4=28px.
   *
   * Depict applies `gap: Npx !important` from column spacing alone — clear the
   * shorthand and set row/column gaps explicitly after seeding the dataset.
   */
  #syncDepictGaps() {
    const depictScript = window['depict-grid-rendering-script'];
    if (!depictScript) return;

    const desktopGutter =
      getComputedStyle(document.documentElement).getPropertyValue('--layout-grid-gutter').trim() || '64px';

    depictScript.dataset.desktopColumnSpacing = desktopGutter;
    depictScript.dataset.desktopRowSpacing = '40px';
    depictScript.dataset.mobileColumnSpacing = desktopGutter;
    depictScript.dataset.mobileRowSpacing = '28px';

    this.#assertGridGaps();
    this.#assertContentBlockImageAlign();
  }

  /**
   * Force axis gaps after Depict's `gap: Npx !important` shorthand.
   * Column gap tracks `--layout-grid-gutter` on all breakpoints so cards stay on the page grid.
   */
  #assertGridGaps() {
    const { grid } = this.refs;
    if (!grid) return;

    const isDesktop = productGridMediaQuery.matches;
    const colGap = 'var(--layout-grid-gutter)';
    const rowGap = isDesktop ? '40px' : '28px';
    const gap = grid.style.getPropertyValue('gap');
    const row = grid.style.getPropertyValue('row-gap');
    const col = grid.style.getPropertyValue('column-gap');
    if (!gap && row === rowGap && col === colGap) return;

    grid.style.removeProperty('gap');
    grid.style.setProperty('column-gap', colGap, 'important');
    grid.style.setProperty('row-gap', rowGap, 'important');
  }

  /**
   * Re-applies density after Section Rendering morphs the grid.
   * Filter HTML only has Liquid defaults; Depict needs `--grid-columns` + classes.
   */
  syncLayoutAfterSectionRender() {
    const { grid } = this.refs;
    if (!grid) return;

    if (!grid.classList.contains('depict-grid')) {
      grid.classList.add('depict-grid', 'desktop-custom-margin', 'mobile-custom-margin');
    }

    const view = grid.getAttribute('product-grid-view');
    if (view === 'default' || isAllowedGridView('desktop', view) || isAllowedGridView('mobile', view)) {
      this.#setLayout(view);
      return;
    }

    this.#applyViewportLayout(currentViewport());
  }

  /**
   * Match Depict merch media to product image height on desktop.
   */
  #assertContentBlockImageAlign() {
    const { grid } = this.refs;
    if (!grid) return;
    syncDepictContentBlockImageAlign(grid);
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
